/* qeslint-disable */
// q@ts-nocheck

import {
    BoundingSphere,
    Cartesian2,
    Cartesian3,
    Cartographic,
    Math as CesiumMath,
    PointPrimitive,
    Scene
} from "cesium";
import * as turf from "@turf/turf";

export function cartesian3ToObject(cartesian3: Cartesian3) {
    return {
        x: cartesian3.x,
        y: cartesian3.y,
        z: cartesian3.z
    };
}

export function newCartesian2FromObject(data: { x: number; y: number }) {
    return new Cartesian2(data.x, data.y);
}

export function newCartesian3FromObject(data: { x: number; y: number; z: number }) {
    return new Cartesian3(data.x, data.y, data.z);
}

export function newCartesian3ArrayFromArray(data: { x: number; y: number; z: number }[]) {
    const ret: Cartesian3[] = [];

    data.forEach((subData) => {
        ret.push(newCartesian3FromObject(subData));
    });

    return ret;
}

export function nearestPointIndex(options: {
    scene: Scene;
    position: Cartesian3;
    points: PointPrimitive[];
    pixelTolerance: number;
}): number {
    const position = options.position;
    const points = options.points;
    const scene = options.scene;
    const pixelTolerance = options.pixelTolerance;

    let minDistance = Number.POSITIVE_INFINITY;
    let minIndex = -1;
    let minPoint = points[0];

    for (let i = 0; i < points.length; i++) {
        const point = points[i];
        const distance = Cartesian3.distance(point.position, position);

        if (distance < minDistance) {
            minDistance = distance;
            minIndex = i;
            minPoint = point;
        }
    }

    const boundingSphere = new BoundingSphere(minPoint.position, minDistance);

    const camera = scene.camera;

    const metersPerPixel = camera.getPixelSize(boundingSphere, scene.drawingBufferWidth, scene.drawingBufferHeight);

    if (minDistance / metersPerPixel < pixelTolerance) {
        return minIndex;
    }

    return -1;
}

export function nearestEdgeIndex(options: {
    scene: Scene;
    position: Cartesian3;
    points: PointPrimitive[];
    isPolygon: boolean;
    pixelTolerance: number;
}) {
    const position = options.position;
    const points = options.points;
    const scene = options.scene;

    const carto = Cartographic.fromCartesian(position);
    const point = turf.point([CesiumMath.toDegrees(carto.longitude), CesiumMath.toDegrees(carto.latitude)]);

    let ret = -1;
    let minDistance = Infinity;

    const edgeCount = options.isPolygon ? points.length : points.length - 1;

    // Iterate over each edge of the polygon
    for (let i = 0; i < edgeCount; i++) {
        // Create a lineString for the current edge
        const startPoint = points[i];

        let endPoint = points[i + 1];

        if (options.isPolygon && i === points.length - 1) {
            endPoint = points[0];
        }

        const startCarto = Cartographic.fromCartesian(startPoint.position);
        const startTurfPoint = turf.point([
            CesiumMath.toDegrees(startCarto.longitude),
            CesiumMath.toDegrees(startCarto.latitude)
        ]);

        const endCarto = Cartographic.fromCartesian(endPoint.position);
        const endTurfPoint = turf.point([
            CesiumMath.toDegrees(endCarto.longitude),
            CesiumMath.toDegrees(endCarto.latitude)
        ]);

        const edge = turf.lineString([startTurfPoint.geometry.coordinates, endTurfPoint.geometry.coordinates]);

        // Find the nearest point on the current edge
        const nearestPoint = turf.nearestPointOnLine(edge, point);

        // Calculate the distance to the nearest point on this edge
        const distance = turf.distance(point, nearestPoint, { units: "meters" });

        // If this edge is closer than the previous closest one, update the values
        if (distance < minDistance) {
            minDistance = distance;
            ret = i;
        }
    }

    const boundingSphere = new BoundingSphere(position, minDistance);

    const camera = scene.camera;

    const metersPerPixel = camera.getPixelSize(boundingSphere, scene.drawingBufferWidth, scene.drawingBufferHeight);

    if (minDistance / metersPerPixel < options.pixelTolerance) {
        return ret;
    }

    return -1;
}
